import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  Alert,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import ConditionBuilder from './ConditionBuilder';
import ActionBuilder from './ActionBuilder';

const LogicRuleEditor = ({
  rule,
  question,
  availableQuestions,
  targetQuestions,
  onSave,
  onCancel,
  loading
}) => {
  const [ruleData, setRuleData] = useState({
    name: '',
    description: '',
    enabled: true,
    priority: 0,
    conditions: [],
    actions: [],
    ...rule
  });
  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    setRuleData({
      name: '',
      description: '',
      enabled: true,
      priority: 0,
      conditions: [],
      actions: [],
      ...rule
    });
  }, [rule]);

  const validateRule = () => {
    const newErrors = {};

    // Validate conditions
    if (!ruleData.conditions || ruleData.conditions.length === 0) {
      newErrors.conditions = 'At least one condition is required';
    } else {
      ruleData.conditions.forEach((condition, index) => {
        if (!condition.questionId) {
          newErrors[`condition_${index}_question`] = 'Question is required';
        }
        if (!condition.operator) {
          newErrors[`condition_${index}_operator`] = 'Condition is required';
        }
        if (condition.value === '' || condition.value === null || condition.value === undefined) {
          // Some operators don't require values
          if (!['answered', 'not_answered'].includes(condition.operator)) {
            newErrors[`condition_${index}_value`] = 'Value is required';
          }
        }
      });
    }

    // Validate actions
    if (!ruleData.actions || ruleData.actions.length === 0) {
      newErrors.actions = 'At least one action is required';
    } else {
      ruleData.actions.forEach((action, index) => {
        if (!action.type) {
          newErrors[`action_${index}_type`] = 'Action type is required';
        }
        if (['show_question', 'hide_question', 'jump_to'].includes(action.type) && !action.targetId) {
          newErrors[`action_${index}_target`] = 'Target question is required';
        }
        if (action.type === 'show_message' && !action.value) {
          newErrors[`action_${index}_message`] = 'Message is required';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateRule()) {
      onSave(ruleData);
    }
  };

  const handleFieldChange = (field, value) => {
    setRuleData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear related errors
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const addCondition = () => {
    const newCondition = {
      id: Date.now().toString(),
      questionId: '',
      operator: 'equals',
      value: '',
      logicOperator: ruleData.conditions.length > 0 ? 'and' : undefined
    };
    
    setRuleData(prev => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }));
  };

  const updateCondition = (index, condition) => {
    setRuleData(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => i === index ? condition : c)
    }));
  };

  const deleteCondition = (index) => {
    setRuleData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const addAction = () => {
    const newAction = {
      id: Date.now().toString(),
      type: 'show_question',
      targetId: '',
      value: ''
    };
    
    setRuleData(prev => ({
      ...prev,
      actions: [...prev.actions, newAction]
    }));
  };

  const updateAction = (index, action) => {
    setRuleData(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => i === index ? action : a)
    }));
  };

  const deleteAction = (index) => {
    setRuleData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  const steps = [
    {
      label: 'Rule Details',
      description: 'Basic information about the rule'
    },
    {
      label: 'Conditions (IF)',
      description: 'Define when this rule should trigger'
    },
    {
      label: 'Actions (THEN)',
      description: 'Define what happens when conditions are met'
    }
  ];

  return (
    <Dialog 
      open={true} 
      onClose={onCancel} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {rule.isNew ? 'Create Logic Rule' : 'Edit Logic Rule'}
          </Typography>
          <IconButton onClick={onCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Stepper activeStep={activeStep} orientation="vertical" sx={{ p: 3 }}>
          {/* Step 1: Rule Details */}
          <Step>
            <StepLabel 
              onClick={() => setActiveStep(0)}
              sx={{ cursor: 'pointer' }}
            >
              {steps[0].label}
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {steps[0].description}
              </Typography>
              
              <Box sx={{ mt: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Rule Name (Optional)"
                  value={ruleData.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="e.g., Show follow-up questions for dissatisfied customers"
                  sx={{ mb: 2 }}
                  error={!!errors.name}
                  helperText={errors.name}
                />
                
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={ruleData.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Describe what this rule does and why it's needed"
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Conditions */}
          <Step>
            <StepLabel 
              onClick={() => setActiveStep(1)}
              sx={{ cursor: 'pointer' }}
              error={!!errors.conditions}
            >
              {steps[1].label}
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {steps[1].description}
              </Typography>

              {errors.conditions && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.conditions}
                </Alert>
              )}

              <Box sx={{ mt: 2, mb: 2 }}>
                {ruleData.conditions.map((condition, index) => (
                  <ConditionBuilder
                    key={condition.id || index}
                    condition={condition}
                    availableQuestions={availableQuestions}
                    currentQuestion={question}
                    onChange={(newCondition) => updateCondition(index, newCondition)}
                    onDelete={() => deleteCondition(index)}
                    showLogicOperator={index > 0}
                    errors={errors}
                    index={index}
                  />
                ))}

                <Button
                  startIcon={<AddIcon />}
                  onClick={addCondition}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add Condition
                </Button>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(2)}
                  sx={{ mt: 1, mr: 1 }}
                  disabled={ruleData.conditions.length === 0}
                >
                  Continue
                </Button>
                <Button
                  onClick={() => setActiveStep(0)}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Back
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Actions */}
          <Step>
            <StepLabel 
              onClick={() => setActiveStep(2)}
              sx={{ cursor: 'pointer' }}
              error={!!errors.actions}
            >
              {steps[2].label}
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {steps[2].description}
              </Typography>

              {errors.actions && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.actions}
                </Alert>
              )}

              <Box sx={{ mt: 2, mb: 2 }}>
                {ruleData.actions.map((action, index) => (
                  <ActionBuilder
                    key={action.id || index}
                    action={action}
                    targetQuestions={targetQuestions}
                    currentQuestion={question}
                    onChange={(newAction) => updateAction(index, newAction)}
                    onDelete={() => deleteAction(index)}
                    errors={errors}
                    index={index}
                  />
                ))}

                <Button
                  startIcon={<AddIcon />}
                  onClick={addAction}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add Action
                </Button>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Button
                  onClick={() => setActiveStep(1)}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Back
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>

        {/* Rule Preview */}
        {ruleData.conditions.length > 0 && ruleData.actions.length > 0 && (
          <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              Rule Preview:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', p: 1, bgcolor: 'white', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              IF {ruleData.conditions.length} condition(s) are met THEN execute {ruleData.actions.length} action(s)
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={loading || ruleData.conditions.length === 0 || ruleData.actions.length === 0}
        >
          {loading ? 'Saving...' : (rule.isNew ? 'Create Rule' : 'Save Changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogicRuleEditor;